export const APP_NAME = 'RecipeCat'

/** 이미지·영상 생성 등 비동기 작업의 상태 */
export const JOB_STATUS = ['queued', 'processing', 'done', 'failed'] as const
export type JobStatus = (typeof JOB_STATUS)[number]

/** 생성 에셋 종류 (영상은 이번 스코프 제외) */
export const ASSET_KIND = ['image'] as const
export type AssetKind = (typeof ASSET_KIND)[number]

/** 재료 단가 KV 캐시 TTL (초) */
export const INGREDIENT_PRICE_CACHE_TTL = 60 * 60 * 24

/**
 * 제품 카테고리. 앱(A1 카드), API(골든셋·시뮬레이션), 어드민이 같은 id 를 쓴다.
 * 이미지는 앱에만 있음 (apps/mobile/src/data/categories.ts).
 */
export const PRODUCT_CATEGORIES = [
  { id: 'meal', label: 'Meals' },
  { id: 'sauce', label: 'Sauce & Seasoning' },
  { id: 'bakery', label: 'Bakery' },
  { id: 'snacks', label: 'Snacks' },
  { id: 'dessert', label: 'Dessert' },
  { id: 'beverage', label: 'Beverage' },
  { id: 'juice', label: 'Juice' },
  { id: 'alcohol', label: 'Alcohol' },
  { id: 'dairy', label: 'Dairy' },
  { id: 'noodles', label: 'Noodles' },
  { id: 'soup-base', label: 'Soup Base' },
  { id: 'processed-meat', label: 'Processed Meat' },
  { id: 'processed-seafood', label: 'Processed Seafood' },
  { id: 'grain-legume-nut', label: 'Grains, Legumes & Nuts' },
  { id: 'fruit-vegetable', label: 'Fruit & Vegetable' },
  { id: 'fermented', label: 'Fermented & Pickled' },
  { id: 'fats-oils', label: 'Fats & Oils' },
  { id: 'other', label: 'Something else' },
] as const
export type ProductCategoryId = (typeof PRODUCT_CATEGORIES)[number]['id']
export const PRODUCT_CATEGORY_IDS = PRODUCT_CATEGORIES.map((c) => c.id) as [
  ProductCategoryId,
  ...ProductCategoryId[],
]

/** 파이프라인 카테고리 이름 (ko·ja·zh). 영어는 PRODUCT_CATEGORIES.label. 앱 결과 화면과 공유 페이지가 같이 쓴다 */
const PRODUCT_CATEGORY_LABELS: Record<'ko' | 'ja' | 'zh', Record<ProductCategoryId, string>> = {
  ko: {
    meal: '식사',
    sauce: '소스·양념',
    bakery: '베이커리',
    snacks: '스낵',
    dessert: '디저트',
    beverage: '음료',
    juice: '주스',
    alcohol: '주류',
    dairy: '유제품',
    noodles: '면',
    'soup-base': '육수·베이스',
    'processed-meat': '육가공',
    'processed-seafood': '수산가공',
    'grain-legume-nut': '곡물·두류·견과',
    'fruit-vegetable': '과일·채소',
    fermented: '발효·절임',
    'fats-oils': '유지',
    other: '기타',
  },
  ja: {
    meal: '食事',
    sauce: 'ソース・調味料',
    bakery: 'ベーカリー',
    snacks: 'スナック',
    dessert: 'デザート',
    beverage: '飲料',
    juice: 'ジュース',
    alcohol: '酒類',
    dairy: '乳製品',
    noodles: '麺',
    'soup-base': 'スープ・だし',
    'processed-meat': '食肉加工',
    'processed-seafood': '水産加工',
    'grain-legume-nut': '穀物・豆・ナッツ',
    'fruit-vegetable': '果物・野菜',
    fermented: '発酵・漬物',
    'fats-oils': '油脂',
    other: 'その他',
  },
  zh: {
    meal: '正餐',
    sauce: '酱料/调味',
    bakery: '烘焙',
    snacks: '零食',
    dessert: '甜点',
    beverage: '饮料',
    juice: '果汁',
    alcohol: '酒类',
    dairy: '乳制品',
    noodles: '面食',
    'soup-base': '汤底',
    'processed-meat': '肉制品',
    'processed-seafood': '水产制品',
    'grain-legume-nut': '谷物/豆类/坚果',
    'fruit-vegetable': '果蔬制品',
    fermented: '发酵/腌渍',
    'fats-oils': '油脂',
    other: '其他',
  },
}

/** 카테고리 이름. language 가 ko·ja·zh 면 그 언어, 그 밖(기본 en)은 영어. 모르는 id 는 그대로 */
export function productCategoryLabel(id: string, language = 'en'): string {
  const en = PRODUCT_CATEGORIES.find((c) => c.id === id)?.label ?? id
  if (language === 'ko' || language === 'ja' || language === 'zh') {
    return PRODUCT_CATEGORY_LABELS[language][id as ProductCategoryId] ?? en
  }
  return en
}

/** 골든셋 목표 개수 (승인된 실제 제품 예시) */
export const GOLDEN_SET_TARGET = 40
